import type { Ref } from 'vue'
import type { AppTextareaEvent, AppTextareaMode, InputPrimaryMode, NativeSelectOption } from '@shared/ui/fields'
import type { DisciplineBalance } from './interfaces'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { DisciplineBalanceAction } from './enums'
import { useDisciplineBalancesStore } from './balances/store'
import { useDisciplineShowApi } from '../api/show.hook'
import { useDisciplineBalancesApi } from '../api/balances.hook'
import { useDisciplineBalanceStoreApi } from '../api/balances-store.hook'
import {
  BALANCE_COMMENT_MAX_LENGTH,
  BALANCE_DEFAULT_REASON,
  BALANCE_LESSONS_MAX,
  BALANCE_LESSONS_MIN,
  getSignedBalanceValue,
  isBalanceLessonsValid,
  mergeCreatedBalance,
} from './discipline-balances'

interface ModalDisciplineBalanceReturn {
  refIsOpen: ComputedRef<boolean>
  refIsSaving: Ref<boolean>
  refAction: Ref<DisciplineBalanceAction>
  refReasonOptions: ComputedRef<NativeSelectOption[]>
  refReason: Ref<string>
  refLessons: Ref<string | number>
  refLessonsMode: ComputedRef<InputPrimaryMode>
  refComment: Ref<string>
  refCommentMode: ComputedRef<AppTextareaMode>
  lessonsMin: number
  lessonsMax: number
  commentMaxLength: number
  setAction: (action: DisciplineBalanceAction) => void
  setReason: (option: NativeSelectOption) => void
  setLessons: (value: string | number) => void
  setComment: (event: AppTextareaEvent) => void
  closeModal: () => void
  submitBalance: () => Promise<void>
}

export const useModalDisciplineBalance = (): ModalDisciplineBalanceReturn => {
  const route = useRoute()
  const toast = useToast()
  const store = useDisciplineBalancesStore()
  const listsStore = useListsStore()

  const refIsSaving = ref(false)
  const refAction = ref<DisciplineBalanceAction>(DisciplineBalanceAction.TopUp)
  const refReason = ref<string>(BALANCE_DEFAULT_REASON)
  const refLessons = ref<string | number>(BALANCE_LESSONS_MIN)
  const refComment = ref<string>('')
  const refIsCommentTooLong = ref(false)

  const refIsOpen = computed<boolean>(() => store.isModalOpen)

  const refReasonOptions = computed<NativeSelectOption[]>(() =>
    Object.entries(listsStore.getLists?.balance.reason_for_agents ?? {}).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refReason.value,
    })))

  const refLessonsCount = computed<number>(() => Number(refLessons.value))

  const refLessonsMode = computed<InputPrimaryMode>(() =>
    isBalanceLessonsValid(refLessonsCount.value) ? 'normal' : 'error')

  const refCommentMode = computed<AppTextareaMode>(() => (refIsCommentTooLong.value ? 'error' : 'normal'))

  const setAction = (action: DisciplineBalanceAction): void => {
    refAction.value = action
  }

  const setReason = (option: NativeSelectOption): void => {
    refReason.value = String(option.value)
  }

  const setLessons = (value: string | number): void => {
    refLessons.value = value
  }

  const setComment = (event: AppTextareaEvent): void => {
    refComment.value = event.value
    refIsCommentTooLong.value = false
  }

  const closeModal = (): void => {
    if (refIsSaving.value) return
    store.setModalOpen(false)
  }

  const resetForm = (): void => {
    refAction.value = DisciplineBalanceAction.TopUp
    refReason.value = BALANCE_DEFAULT_REASON
    refLessons.value = BALANCE_LESSONS_MIN
    refComment.value = ''
    refIsCommentTooLong.value = false
  }

  const refreshBalances = async (disciplineId: string, created: DisciplineBalance): Promise<void> => {
    await useDisciplineBalancesApi({ disciplineId })

    if (store.getCurrentPage === 1 && !store.hasFilters) {
      store.setBalances(mergeCreatedBalance(store.getBalances, created))
    }
  }

  /** The header shows the lesson balance too, so both are refreshed before the modal lets go. */
  const refreshAfterStore = async (disciplineId: string, created: DisciplineBalance): Promise<void> => {
    await Promise.all([
      refreshBalances(disciplineId, created),
      useDisciplineShowApi({ disciplineId, silent: true }),
    ])
  }

  const submitBalance = async (): Promise<void> => {
    if (refIsSaving.value || !isBalanceLessonsValid(refLessonsCount.value)) return

    if (refComment.value.length > BALANCE_COMMENT_MAX_LENGTH) {
      refIsCommentTooLong.value = true
      toast.warning('The comment is too long.')
      return
    }

    const disciplineId = route.params.id as string

    // The modal keeps its loader until the history and the header show the new balance; a refused write closes it at once.
    refIsSaving.value = true

    try {
      const created = await useDisciplineBalanceStoreApi({
        disciplineId,
        payload: {
          value: getSignedBalanceValue(refAction.value, refLessonsCount.value),
          reason: refReason.value,
          comment: refComment.value.trim() || null,
        },
      })

      if (created) await refreshAfterStore(disciplineId, created)
    } finally {
      refIsSaving.value = false
      store.setModalOpen(false)
    }
  }

  watch(refIsOpen, (isOpen) => {
    if (isOpen) resetForm()
  })

  return {
    refIsOpen,
    refIsSaving,
    refAction,
    refReasonOptions,
    refReason,
    refLessons,
    refLessonsMode,
    refComment,
    refCommentMode,
    lessonsMin: BALANCE_LESSONS_MIN,
    lessonsMax: BALANCE_LESSONS_MAX,
    commentMaxLength: BALANCE_COMMENT_MAX_LENGTH,
    setAction,
    setReason,
    setLessons,
    setComment,
    closeModal,
    submitBalance,
  }
}
